import { ConfirmationModal, Notification, scrollToElement } from "@r360/library";
import React, { createRef, useEffect, useMemo, useRef, useState } from "react";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import useTranslate from "../../hooks/useTranslate";
import {
    createUserGuest,
    disconnectGuestFromUser,
    fetchGuestsOnUser,
    updateUserGuest,
} from "../../store/actions/guests";
import { TGuest } from "../../store/types";
import { GuestForm } from "./GuestForm";
import { GuestList } from "./GuestList";
import "./MyGuests.scss";

const MyGuests = () => {
    const dispatch = useAppDispatch();
    const t = useTranslate();

    const userToken = useAppSelector(state => state.account.token);
    const guests: { [key: string]: TGuest } = useAppSelector(state => state.guests.guests);
    const cartIsEmpty = useAppSelector(state => !state.checkoutCart?.products.length);
    const customerOptions = useAppSelector(state => state.clientData.options.customer);
    const { requestsLoading } = useAppSelector(state => state.axiosStatus);
    const updatingGuestInformationInProgress: boolean = requestsLoading["updateUserGuest"];
    const creatingNewGuestGuestInProgress: boolean = requestsLoading["createUserGuest"];
    const deleteGuestInProgress: boolean = requestsLoading["removeGuestOnUser"];
    const { isMobile, isTablet, isDesktop, isIframe, iframeOffsetTop } = useAppSelector(state => state.window);

    const [guestToEdit, setGuestToEdit] = useState<string | null>(null);
    const [guestToRemove, setGuestToRemove] = useState<TGuest | null>(null);
    const [newGuestIsOpen, setNewGuestIsOpen] = useState(false);
    const newGuestRef = useRef<any | null>(null);
    const nonPrimaryGuests = Object.entries(guests).filter(([, guest]) => !guest.primary);

    useEffect(() => {
        if (Object.keys(guests).length === 0 && userToken) {
            dispatch(fetchGuestsOnUser(userToken));
        }
        // eslint-disable-next-line
    }, [dispatch, userToken]);

    // Close the new guest form when finished adding a new guest
    useEffect(() => {
        if (!creatingNewGuestGuestInProgress && newGuestIsOpen) {
            setNewGuestIsOpen(false);
        }
        // eslint-disable-next-line
    }, [creatingNewGuestGuestInProgress]);

    const handleSubmit = (guestId: string, guestValues: any) => {
        dispatch(updateUserGuest(userToken, guestValues));
        guestRefs[guestId].form.current.resetTouched();
    };

    const handleNewGuestSubmit = (guestValues: any) => {
        dispatch(createUserGuest(userToken, guestValues));
        newGuestRef.current.resetForm();
    };

    // Create a reference for each guest that will be used to connect to their forms.
    const guestRefs = useMemo(() => {
        return Object.entries(guests)
            .filter(([, guest]) => !guest.primary)
            .reduce((acc: { [key: string]: { form: any } }, [guestId]) => {
                acc[guestId] = {
                    form: createRef(),
                };

                return acc;
            }, {});
        // eslint-disable-next-line
    }, [Object.keys(guests).length]);

    const handleRemoveGuest = () => {
        if (guestToRemove) {
            dispatch(disconnectGuestFromUser(guestToRemove.id, userToken))
                .then(() => {
                    setGuestToEdit(null);
                })
                .catch((err: any) => {
                    console.error(err);
                })
                .finally(() => {
                    setGuestToRemove(null);
                });
        }
    };

    const toggleGuestToEdit = (guestId: string) => {
        if (!cartIsEmpty) {
            return;
        }

        if (guestId === guestToEdit) {
            setGuestToEdit(null);
        } else {
            setGuestToEdit(guestId);
            scrollToElement("edit-guest-form", undefined, isMobile ? "start" : "center");
        }

        if (newGuestIsOpen) {
            setNewGuestIsOpen(false);
        }
    };

    const toggleNewGuestIsOpen = () => {
        if (!cartIsEmpty) {
            return;
        }

        if (!newGuestIsOpen) {
            scrollToElement("edit-guest-form", undefined, isMobile ? "start" : "center");
        }
        setNewGuestIsOpen(prevState => !prevState);

        if (guestToEdit) {
            setGuestToEdit(null);
        }
    };

    const handleCancelEdit = (guestId: string) => {
        setGuestToEdit(null);
        const guestButton = document.getElementById(`guest-${guestId}`);
        if (guestButton) {
            guestButton.focus();
        }
    };

    return (
        <>
            <Notification type="info" className="u-mb-24">
                <p>{t("book.my_pages.my_guests.info")}</p>
            </Notification>
            <Notification type="warning" show={!cartIsEmpty} className="u-mb-24">
                <p>{t("book.my_pages.my_guests.ongoing_booking")}</p>
            </Notification>
            <GuestList
                guests={nonPrimaryGuests}
                guestToEdit={guestToEdit}
                newGuestIsOpen={newGuestIsOpen}
                cartIsEmpty={cartIsEmpty}
                toggleGuestToEdit={toggleGuestToEdit}
                toggleNewGuestIsOpen={toggleNewGuestIsOpen}
            />
            <GuestForm
                guestToEdit={guestToEdit}
                newGuestIsOpen={newGuestIsOpen}
                guests={nonPrimaryGuests}
                updatingGuestInformationInProgress={updatingGuestInformationInProgress}
                creatingNewGuestGuestInProgress={creatingNewGuestGuestInProgress}
                guestRefs={guestRefs}
                newGuestRef={newGuestRef}
                handleSubmit={handleSubmit}
                onCancelEdit={handleCancelEdit}
                setGuestToRemove={setGuestToRemove}
                setNewGuestIsOpen={setNewGuestIsOpen}
                handleNewGuestSubmit={handleNewGuestSubmit}
                customerOptions={customerOptions}
                isMobile={isMobile}
            />
            <ConfirmationModal
                open={guestToRemove}
                heading={t("book.checkout.remove_guest")}
                description={t("book.confirmations.remove", [`${guestToRemove?.firstname} ${guestToRemove?.lastname}`])}
                confirmBtnText={t("book.general.ok")}
                confirmBtnLoading={deleteGuestInProgress}
                cancelBtnText={t("book.general.cancel")}
                removing={true}
                onClose={() => setGuestToRemove(null)}
                onConfirmClick={handleRemoveGuest}
                onCancelClick={() => {
                    setGuestToRemove(null);
                }}
                {...(isIframe && isDesktop && { fromTop: 120 + iframeOffsetTop + "px" })}
                {...(isIframe &&
                    (isMobile || isTablet) && {
                        fromTop: 80 + iframeOffsetTop + "px",
                    })}
            />
        </>
    );
};

export default MyGuests;
